import GoogleMapReact, { Coords } from "google-map-react";
import React from "react";
import * as ReactDOM from "react-dom";
import { RouteComponentProps, withRouter } from "react-router";

import Config from "config";
import { Store } from "interfaces/store";
import { ConnectedProps, connect } from "react-redux";
import { State } from "state";

interface NearbyStoresGoogleMapProps extends RouteComponentProps {
  currentZip: string;
  stores: Store[];
  position: Coords;
}

interface LocationLatLng {
  lat: () => number;
  lng: () => number;
}

interface NearbyStoresGoogleMapState {
  googleApiKey: string;
}

class NearbyStoresGoogleMap extends React.Component<
  NearbyStoresGoogleMapProps & PropsFromRedux,
  NearbyStoresGoogleMapState
> {
  // Kept alive across renders so markers can be updated without remounting the map.
  private mapInstance: any = null;
  private mapsInstance: any = null;
  private infowindowInstance: any = null;
  private markers: any[] = [];
  // Incremented on every addMarkers call so stale geocoding callbacks are ignored.
  private markerGeneration = 0;

  /** Only when exactly one pin: zoom out after fitting (street-level first). */
  private static readonly ZOOM_OUT_LEVELS = 15;
  private static readonly ZOOM_OUT_MIN = 7;

  /** Two or more pins: normal view — fit every pin in the frame, centered; no zoom-out. */
  private static readonly FIT_PADDING_MULTI_PINS_PX = 60;

  private zoomOutAfterFit() {
    if (!this.mapInstance || !this.mapsInstance) return;
    const map = this.mapInstance;
    const maps = this.mapsInstance;
    maps.event.addListenerOnce(map, "idle", () => {
      const z = map.getZoom();
      if (z == null) return;
      const next = Math.max(
        z - NearbyStoresGoogleMap.ZOOM_OUT_LEVELS,
        NearbyStoresGoogleMap.ZOOM_OUT_MIN
      );
      if (next !== z) map.setZoom(next);
    });
  }

  constructor(props: any) {
    super(props);
    this.state = {
      googleApiKey: Config.GOOGLE_MAPS_API_KEY,
    };
  }

  componentDidUpdate(prevProps: NearbyStoresGoogleMapProps & PropsFromRedux) {
    // Stores changed (new page, new search) — redraw markers and refit bounds.
    if (prevProps.stores !== this.props.stores) {
      this.addMarkers(this.props.stores);
    }

    // User location / searched zip changed — pan without remounting the whole map.
    // addMarkers will call fitBounds when stores exist; panTo acts as fallback when
    // there are no stores to show (empty search result).
    const positionChanged =
      prevProps.position.lat !== this.props.position.lat ||
      prevProps.position.lng !== this.props.position.lng;
    if (positionChanged && this.mapInstance) {
      this.mapInstance.panTo(this.props.position);
    }
  }

  public formatStoreAddress = (address: string): { firstLine: string; secondLine: string } => {
    const splitAddress = address.split(",");
    const firstLine = splitAddress.slice(0, -3).join(",");
    const secondLine = splitAddress.slice(-3).join(",");
    return { firstLine, secondLine };
  };

  public StoreInfo: React.FunctionComponent<{ store: Store }> = ({ store }) => {
    const { history } = this.props;
    const storeUrl = `/projects/${store.project_id}/stores/${store.id}`;
    return (
      <div className="body-link" onClick={() => history.push(storeUrl)}>
        {store.name} <br />
        {this.formatStoreAddress(store.store_address).firstLine}
        <br />
        {this.formatStoreAddress(store.store_address).secondLine}
      </div>
    );
  };

  public getMarker(location: LocationLatLng, map: any, maps: any) {
    return new maps.Marker({
      position: { lat: location.lat(), lng: location.lng() },
      map,
    });
  }

  public addInfowindowClickListener(
    map: any,
    maps: any,
    marker: any,
    infowindow: any,
    store: Store
  ) {
    maps.event.addListener(
      marker,
      "click",
      ((markerItem: object) => {
        return () => {
          const resetInfowindowContent = new Promise<void>((resolve) => {
            infowindow.setContent('<div id="info-window-wrapper"></div>');
            infowindow.open(map, markerItem);
            resolve();
          });
          resetInfowindowContent.then(() => {
            ReactDOM.render(
              <this.StoreInfo store={store} />,
              document.getElementById("info-window-wrapper")
            );
          });
        };
      })(marker)
    );
  }

  /** Remove all current markers from the map */
  private clearMarkers() {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  private normalizeAddress = (address: string) =>
    address?.replace(/\s+/g, " ").replace(/,\s*,/g, ",").trim().toLowerCase();

  private uniqueStoresReducer = (stores: any[]) => {
    const seen = new Set();

    return stores.reduce((acc: any[], store) => {
      const normalized = this.normalizeAddress(`${store.retail_store_num}-${store.store_address}`);

      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        acc.push(store);
      }

      return acc;
    }, []);
  };

  /**
   * Geocode each store's address, place a marker, and — once ALL geocoding
   * requests for this batch are settled — fit the map viewport to contain
   * every successfully placed pin.
   *
   * A generation counter ensures that callbacks from a previous (cleared)
   * batch are silently discarded when a new batch starts (e.g. pagination).
   */
  private addMarkers(stores: Store[]) {
    if (!this.mapInstance || !this.mapsInstance) return;

    this.clearMarkers();

    if (stores.length === 0) return;

    // Bump generation so any in-flight callbacks from the previous batch are ignored.
    const generation = ++this.markerGeneration;

    const geocoder = new this.mapsInstance.Geocoder();
    const bounds = new this.mapsInstance.LatLngBounds();
    let pending = stores.length;
    const zips = new Set();
    // let placed = 0;

    const onSettled = () => {
      pending--;
      if (pending > 0) return;

      // All geocoding responses received for this generation.
      if (generation !== this.markerGeneration) return; // superseded, skip
      const zipArray = Array.from(zips);
      console.log("zipArray", zipArray);
      if (zipArray.length === 0) return;
      // if (placed === 0) return; // no pins geocoded successfully, map stays as-is

      // Multiple pins: standard map — fit all markers in view, centered (no follow-up zoom-out).
      if (zipArray.length > 1) {
        this.mapInstance.fitBounds(bounds, NearbyStoresGoogleMap.FIT_PADDING_MULTI_PINS_PX);
        return;
      }
      // if (placed > 1) {
      //   this.mapInstance.fitBounds(bounds, NearbyStoresGoogleMap.FIT_PADDING_MULTI_PINS_PX);
      //   return;
      // }

      // Single pin: street zoom then zoom-out.
      this.mapInstance.fitBounds(bounds);
      this.mapInstance.setZoom(14);
      this.zoomOutAfterFit();
    };

    const remvoeDuplicateStores = this.uniqueStoresReducer(stores);
    console.log("remvoeDuplicateStores", remvoeDuplicateStores, stores)

    remvoeDuplicateStores.forEach((store) => {
      geocoder.geocode({ address: store.store_address }, (results: any, status: any) => {
        // Discard stale callbacks from a previous batch.
        if (generation !== this.markerGeneration) return;

        if (status === "OK") {
          const location = results[0].geometry.location;
          const marker = this.getMarker(location, this.mapInstance, this.mapsInstance);
          this.markers.push(marker);
          bounds.extend(location);

          zips.add(`${store.zip}-${store.store_address}`);
          // placed++;
          this.addInfowindowClickListener(
            this.mapInstance,
            this.mapsInstance,
            marker,
            this.infowindowInstance,
            store
          );
        }

        onSettled();
      });
    });
  }

  public handleApiLoaded(map: any, maps: any) {
    // Save instances so componentDidUpdate can update markers at any time.
    this.mapInstance = map;
    this.mapsInstance = maps;
    this.infowindowInstance = new maps.InfoWindow();

    this.addMarkers(this.props.stores);
  }

  public render() {
    const { googleApiKey } = this.state;
    if (!googleApiKey) return <></>;
    return (
      <div className="nearby-stores-map-container" data-qa="nearby-stores-map">
        <GoogleMapReact
          bootstrapURLKeys={{ key: googleApiKey, language: "en" }}
          defaultCenter={this.props.position}
          defaultZoom={11}
          yesIWantToUseGoogleMapApiInternals={true}
          onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
        />
      </div>
    );
  }
}

const connector = connect((state: State) => ({
  config: state.config,
}));

type PropsFromRedux = ConnectedProps<typeof connector>;

export default withRouter(connector(NearbyStoresGoogleMap));
